import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import { Shimmer, ShimmerPlaceholder } from '../../../../ui';
import crownIcon from '../../../../../assets/loyalty/crown.svg?reference';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { SolidContentWrap } from '../../../shared/components';
import {
	ProgressBar,
	StyledPointsProgressBar,
} from '../../../shared/components/progress-bar/ProgressBar';
import { CurrentTierPointsTitle } from './CurrentTierPointsTitle';
import { StyledTitleShimmer } from './UserCurrentTier';
import { useLoyaltyAccountStore } from '../../domain/stores/useLoyaltyAccountStore';
import { Color } from '../../../shared/styles/Color';
import { formatDate } from '../../../shared/utils/formatDate';
import { useLoyaltyTierStore } from '../../domain/stores/useLoyaltyTierStore';
import { useFormatNumberWithSeparator } from '../../../shared/utils/useFormatNumberWithSeparator';
import Responsive from '../../../../ui/utils/Responsive';

export const UserCurrentPoints = observer(() => {
	const { formatWithSeparator } = useFormatNumberWithSeparator();
	const { t } = useTranslation();
	const { loyaltyDetails, isLoading } = useLoyaltyAccountStore();
	const { contentfulLastTierDetails, contentfulNextTierDetails } =
		useLoyaltyTierStore();
	if (isLoading) {
		return <StyledUserCurrentPointsShimmer />;
	}

	if (!loyaltyDetails) {
		return null;
	}

	const isNewLoyaltyUser = !loyaltyDetails.rewardTier;
	const isLatestTier = !loyaltyDetails.nextTier;
	const isPointsToRetain = !!loyaltyDetails.pointsToRetainRewardTier;
	const isPointsToNextTier = !!loyaltyDetails.pointsToNextTier;
	const isPendingPoints = !!loyaltyDetails.pendingPoints;
	const isFirstCollectionPeriod = !loyaltyDetails.tierFromLastPeriod;

	const showSecuredCopy =
		!isNewLoyaltyUser &&
		isLatestTier &&
		(isFirstCollectionPeriod || !isPointsToRetain);

	const showEarnCopy =
		!isNewLoyaltyUser &&
		!isLatestTier &&
		(isFirstCollectionPeriod || !isPointsToRetain);

	const showRetainCopy =
		!isNewLoyaltyUser && !isFirstCollectionPeriod && isPointsToRetain;

	const showProgressBar =
		(!showSecuredCopy && (isPointsToRetain || isPointsToNextTier)) ||
		isNewLoyaltyUser;

	const redeemablePoints = formatWithSeparator(loyaltyDetails.redeemablePoints);
	return (
		<StyledUserCurrentPointsContainer data-testid="user-current-points">
			<StyledUserCurrentPointsHeader>
				<StyledCurrentPointsImage src={crownIcon} alt="crown" />
				<CurrentTierPointsTitle
					title={t('loyalty_current_points_label', {
						number: redeemablePoints,
					})}
				/>

				{isPendingPoints && (
					<StyledDescriptionText>
						<strong>
							{t('loyalty_pending_points_label', {
								number: formatWithSeparator(loyaltyDetails.pendingPoints),
							})}
						</strong>
					</StyledDescriptionText>
				)}
			</StyledUserCurrentPointsHeader>

			{showProgressBar && (
				<ProgressBar
					progress={loyaltyDetails.redeemablePoints || 0}
					pending={loyaltyDetails.pendingPoints}
					remaining={
						isLatestTier
							? loyaltyDetails.pointsToRetainRewardTier
							: loyaltyDetails.pointsToNextTier
					}
				/>
			)}

			{isNewLoyaltyUser && (
				<p>
					<StyledDescriptionText>
						{t('loyalty_no_tier_information_message')}
					</StyledDescriptionText>
				</p>
			)}

			{showSecuredCopy && (
				<p>
					<StyledDescriptionText>
						{t('loyalty_reached_latest_tier_message', {
							tier: loyaltyDetails?.rewardTier || loyaltyDetails?.tier,
						})}
					</StyledDescriptionText>
				</p>
			)}

			{(showEarnCopy || showRetainCopy) && (
				<p>
					<StyledDescriptionText>
						{t('loyalty_retain_points_earn_label')}
					</StyledDescriptionText>
					<StyledDescriptionHighlitedText>
						{t('loyalty_current_points_label', {
							number: showRetainCopy
								? formatWithSeparator(loyaltyDetails.pointsToRetainRewardTier)
								: formatWithSeparator(loyaltyDetails.pointsToNextTier),
						}).toLowerCase()}
					</StyledDescriptionHighlitedText>

					{!!loyaltyDetails.collectionPeriodEnd && (
						<>
							<StyledDescriptionText>
								{t('loyalty_retain_points_earn_before_label')}
							</StyledDescriptionText>
							<StyledDescriptionHighlitedText>
								{formatDate(
									new Date(loyaltyDetails.collectionPeriodEnd),
									'MMMM dd, yyyy',
								)}
							</StyledDescriptionHighlitedText>
						</>
					)}
					<StyledDescriptionText>
						{showRetainCopy
							? t('loyalty_retain_current_tier_text', {
									tier: contentfulLastTierDetails?.title,
							  })
							: t('loyalty_reach_next_tier_text', {
									nextTier: contentfulNextTierDetails?.title,
							  })}
					</StyledDescriptionText>
				</p>
			)}
		</StyledUserCurrentPointsContainer>
	);
});

const StyledUserCurrentPointsContainer = styled(SolidContentWrap)`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	flex-grow: 2;
	padding: 24px;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: 100%;
		gap: 12px;
		padding: 16px;
	}
`;

const StyledUserCurrentPointsHeader = styled.div`
	height: 38px;
	display: flex;
	align-items: center;
	gap: 6px;
`;

const StyledCurrentPointsImage = styled.img`
	display: block;
`;

const StyledDescriptionText = styled.span`
	margin: 0;
	font-size: 18px;
	color: ${Color.Background2};
	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		font-size: 15px;
	}
`;
const StyledDescriptionHighlitedText = styled(StyledDescriptionText)`
	color: ${Color.Background};
	font-weight: 700;
`;

const TextShimmer = styled(ShimmerPlaceholder)`
	height: 12px;
`;
const StyledUserCurrentPointsShimmer = () => (
	<StyledUserCurrentPointsContainer>
		<StyledTitleShimmer />
		<Shimmer>
			<StyledPointsProgressBar />
		</Shimmer>
		<TextShimmer />
	</StyledUserCurrentPointsContainer>
);
